import React, { useState, useEffect } from 'react'
import { Info, Loader2 } from 'lucide-react'
import api from 'config/axiosConfig'
import Modal from 'ui/Modal'
import InputField from 'ui/InputField'
import { Team } from 'util/constants'
import { toast } from 'sonner'
import RESTRICTED_WORD_LIST from 'config/restrictedWordList';
import { apiRoutes } from "util/apiRoutes";

interface Props {
    team: Team;
    onClose: () => void;
}

const TEAMNAME_REQUIREMENTS = {
    minLength: 2,
    maxLength: 30
};

const TeamnameModal: React.FC<Props> = ({ team, onClose }) => {
    const [teamname, setTeamname] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidTeamname, setIsValidTeamname] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string>('');
    const validateTeamname = async (teamname: string): Promise<void> => {
        setIsValidTeamname(true);
        setValidationMessage('');
        
        if (
            /^[a-z0-9_.]{2,30}$/.test(teamname) &&
            !/\.{2,}/.test(teamname) &&
            !teamname.startsWith(".") &&
            !teamname.startsWith("_") &&
            !teamname.endsWith(".") &&
            !teamname.endsWith("_") &&
            !teamname.includes("..") &&
            !teamname.includes("__") &&
            !/^admin/.test(teamname) &&
            !/admin$/.test(teamname) &&
            !RESTRICTED_WORD_LIST.includes(teamname)
        ) {
            try {
                // Check for existing team with this name
                const teamResponse = await api.get(apiRoutes.team.byName(teamname));
                if (teamResponse.status !== 404) {
                    setIsValidTeamname(false);
                    setValidationMessage('Partea name already taken.');
                    return;
                }

                // Check for existing user with this name
                const userResponse = await api.get(apiRoutes.user.byUsername(teamname));
                if (userResponse.status !== 404) {
                    setIsValidTeamname(false);
                    setValidationMessage('Partea name conflicts with existing username.');
                    return;
                }

                setIsValidTeamname(true);
                setValidationMessage('');
            } catch (error: any) {
                if (error.response?.status === 404) {
                    setIsValidTeamname(true);
                    setValidationMessage('');
                } else {
                    setIsValidTeamname(false);
                    setValidationMessage('Error validating Partea name.');
                }
            }
        } else {
            if (teamname.startsWith(".") ||
                teamname.startsWith("_") ||
                teamname.endsWith(".") ||
                teamname.endsWith("_")
            ) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name must not start/end with '.' or '_'`);
            } else if (/^admin/.test(teamname) ||
                /admin$/.test(teamname) ||
                RESTRICTED_WORD_LIST.includes(teamname)
            ) {
                setIsValidTeamname(false);
                setValidationMessage('Partea name is not allowed.');
            } else if (!/^[a-z0-9_.]{2,30}$/.test(teamname)) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name may not contain special characters other than '.' and '_'`);
            } else if (teamname.includes("..") ||
                teamname.includes("__")
            ) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name may not contain consecutive '.' or '_'`);
            }
        }
    };

    useEffect(() => {
        if (teamname && teamname.length > 1) {
            validateTeamname(teamname);
        } else if (teamname && 
            (teamname.startsWith(".") ||
            teamname.startsWith("_"))
        ) {
            setIsValidTeamname(false);
            setValidationMessage(`Partea name must not start with '.' or '_'`);
        } else if (teamname && teamname.length < TEAMNAME_REQUIREMENTS.minLength) {
            setIsValidTeamname(false);
            setValidationMessage(`Partea name must be at least ${TEAMNAME_REQUIREMENTS.minLength} characters long.`);
        } else {
            setValidationMessage('');
        }
    }, [teamname]);

    const handleTeamnameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const lowercaseTeamname = e.target.value.toLowerCase();
        setTeamname(lowercaseTeamname);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);
        try {
            await api.patch(apiRoutes.team.teamname(team.id), { teamname });
            if (team.teamname) {
                team.teamname = teamname;
            }
            toast.success('Partea name updated successfully!');
            window.location.href = `/partea/${teamname}/settings/profile`;
            onClose();
        } catch (error) {
            console.error('Error updating Partea name:', error);
            setValidationMessage('Failed to update Partea name. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = (): void => {
        onClose();
    };

    const isButtonDisabled = (!teamname || !isValidTeamname) ||
        isSubmitting || 
        teamname.length < TEAMNAME_REQUIREMENTS.minLength;

    const footer = (
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
            <button
                type="button"
                onClick={onClose}
                className="btn-secondary w-full sm:w-fit py-2 px-4"
            >
                Cancel
            </button>
            <button
                type="submit"
                onClick={handleSubmit}
                disabled={isButtonDisabled}
                className={`w-full sm:w-fit py-2 px-4 ${
                    isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
            </button>
        </div>
    );

    return (
        <Modal isOpen={true} onClose={handleClose} title="Set your Partea name" footer={footer} stickyFooter>
            <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Partea names are publicly visible.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        <div className="form-control">
                            <div className="relative">
                                <InputField
                                    type="text"
                                    id="teamname"
                                    name="teamname"
                                    labelText="Partea name"
                                    fieldRequired
                                    placeholder={team.teamname || ''}
                                    value={teamname}
                                    onChange={handleTeamnameChange}
                                    validationState={isValidTeamname && teamname.length >= TEAMNAME_REQUIREMENTS.minLength ? 'valid' : teamname && !isValidTeamname ? 'invalid' : 'none'}
                                    error={validationMessage}
                                    spellCheck={false}
                                    maxLength={TEAMNAME_REQUIREMENTS.maxLength}
                                    minLength={TEAMNAME_REQUIREMENTS.minLength}
                                />

                                {error && (
                                    <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                        <Info className="mr-1 size-4" />
                                        <span>{error}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>
                </div>

                {/* Action buttons moved to Modal footer */}
            </div>
        </Modal>
    );
};

export default TeamnameModal; 